import { useContext, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { Icon as FactorialOneIcon } from '@factorialco/factorial-one-react'
import {
  BankingEntitiesTransactionTypeEnum,
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useTransactionDataTable,
} from 'generated/resources/datatable.graphql'
import {
  AccountProviderConfigurationFragment,
  BankingTransactionFilter,
  BankingValueObjectsCardCreationSourceEnum,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { AccountActionsContext } from 'modules/banking/contexts/AccountActionsContext'
import { ImportTransactionsContext } from 'modules/banking/contexts/ImportTransactionsContext'
import { ProviderConfigurationContext } from 'modules/banking/contexts/ProviderConfigurationContext'
import { icon as cardIcon } from 'modules/banking/lib/cards/icon'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

import { AccountTransactionsInsights } from '../AccountTransactionsInsights'

const EmptyBlock = ({
  providerConfiguration,
}: {
  providerConfiguration: AccountProviderConfigurationFragment | null | undefined
}) => {
  const bankAggregation = providerConfiguration?.services.includes('bank_aggregation')
  const message = bankAggregation
    ? i18n.t('banking.accounts.importing_transactions_content')
    : i18n.t('banking.accounts.transactions_empty_message')

  return (
    <Box width="full">
      <Box flexDirection="column" gap="s32" alignItems="center">
        <Box
          alignItems="center"
          maxWidth="s400"
          textAlign="center"
          style={{ maxWidth: '500px' }}
          gap="s8"
        >
          {bankAggregation && (
            <Text.Header2>{i18n.t('banking.accounts.importing_transactions_title')}</Text.Header2>
          )}
          <Text.Normal color="grey800">{message}</Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}

const typeIcon = (
  type: BankingEntitiesTransactionTypeEnum | undefined | null,
  last4: string | undefined | null,
  creationSource: BankingValueObjectsCardCreationSourceEnum | undefined | null
) => {
  const iconName = !!creationSource ? cardIcon(creationSource) : undefined

  switch (type) {
    case 'topup':
      return (
        <>
          <Box width="s24" height="s24">
            <Icon.Large icon="top-up" color="viridian1000" />
          </Box>
          <Text.Normal size="200">{i18n.t('banking.accounts.transaction_type.topup')}</Text.Normal>
        </>
      )
    case 'payroll':
      return (
        <>
          <Icon.Medium icon="payroll" color="viridian1000" />
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.payroll')}
          </Text.Normal>
        </>
      )
    case 'card_payment':
      return (
        <>
          {iconName && (
            <Box width="s24" height="s24">
              <Text.Normal color="viridian1000">
                <FactorialOneIcon size="lg" icon={iconName} />
              </Text.Normal>
            </Box>
          )}
          <Text.Normal size="200">
            {!last4 && i18n.t('banking.accounts.transaction_type.card_payment')}
            {last4 &&
              i18n.t('banking.transactions.types.card_payment', { cardLast4numbers: last4 })}
          </Text.Normal>
        </>
      )
    case 'dispute':
      return (
        <>
          {iconName && (
            <Box width="s24" height="s24">
              <Text.Normal color="viridian1000">
                <FactorialOneIcon size="lg" icon={iconName} />
              </Text.Normal>
            </Box>
          )}
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.dispute')}
          </Text.Normal>
        </>
      )
    case 'incoming':
      return (
        <>
          <Box width="s24" height="s24">
            <Icon.Large icon="top-up" color="viridian1000" />
          </Box>
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.incoming')}
          </Text.Normal>
        </>
      )
    case 'fees':
      return <Text.Normal size="200">{i18n.t('banking.accounts.transaction_type.fee')}</Text.Normal>
    case 'card_refund':
      return (
        <>
          {iconName && (
            <Box width="s24" height="s24">
              <Text.Normal color="viridian1000">
                <FactorialOneIcon size="lg" icon={iconName} />
              </Text.Normal>
            </Box>
          )}
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.card_refund')}
          </Text.Normal>
        </>
      )
    case 'outgoing':
      return (
        <>
          <Icon.Medium icon="payroll" color="viridian1000" />
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.outgoing')}
          </Text.Normal>
        </>
      )
    case 'incoming_transfer':
      return (
        <>
          <Box width="s24" height="s24">
            <Icon.Large icon="top-up" color="viridian1000" />
          </Box>
          <Text.Normal size="200">
            {i18n.t('banking.accounts.transaction_type.incoming_transfer')}
          </Text.Normal>
        </>
      )
    default:
      return (
        <Text.Normal size="200">{i18n.t('banking.accounts.transaction_type.unknown')}</Text.Normal>
      )
  }
}

const AccountTransactionsDataTable = () => {
  const {
    access: { locale },
  } = useCurrentSession()
  const [description, setDescription] = useState('')
  const { navigation } = useNavigation()
  const { id: bankAccountId } = useParams<{
    id: string
  }>()
  const { newNumTransactions } = useContext(ImportTransactionsContext)
  const { reconcileCTA, importTransactionsCTA, exportTransactionsCTA, currency } =
    useContext(AccountActionsContext)
  const { configuration } = useContext(ProviderConfigurationContext)
  const [refreshTimes, setRefreshTimes] = useState<number>(0)

  const transactionTypeFilter: DataTableFilter<BankingTransactionFilter>[] = [
    {
      identifier: 'transactionBookingDateFilter',
      title: i18n.t('banking.attributes.booking_date'),
      path: 'bookingDate',
      default: false,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DateTimePicker',
          operation: 'between',
          value: undefined,
        },
      ],
    },
    {
      identifier: 'transactionTypeFilter',
      title: i18n.t('banking.attributes.type'),
      path: 'type',
      default: true,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('banking.accounts.transaction_type.topup'),
              value: 'topup',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.card_payment'),
              value: 'card_payment',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.card_refund'),
              value: 'card_refund',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.dispute'),
              value: 'dispute',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.outgoing'),
              value: 'outgoing',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.incoming'),
              value: 'incoming',
            },
            {
              label: i18n.t('banking.accounts.transaction_type.incoming_transfer'),
              value: 'incoming_transfer',
            },
          ],
        },
      ],
    },
    {
      identifier: 'cardCreationSourceFilter',
      title: i18n.t('banking.attributes.card_creation_source'),
      path: 'cardCreationSource',
      default: false,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('banking.accounts.card_creation_source.expenses'),
              value: 'expenses',
            },
            {
              label: i18n.t('banking.accounts.card_creation_source.software'),
              value: 'software',
            },
            {
              label: i18n.t('banking.accounts.card_creation_source.procurement'),
              value: 'procurement',
            },
            {
              label: i18n.t('banking.accounts.card_creation_source.benefits'),
              value: 'salary',
            },
          ],
        },
      ],
    },
  ]

  const source = useTransactionDataTable(
    [
      {
        identifier: 'bookingDate',
        title: i18n.t('banking.attributes.booking_date'),
        kind: ProjectionKind.Custom,
        template: '{{ bookingDate }}',
        render: ({ bookingDate }) => {
          return <Text.Normal size="200">{dateBackendDateTimeToUI(bookingDate)}</Text.Normal>
        },
        width: 90,
      },
      {
        identifier: 'type',
        title: i18n.t('banking.attributes.type'),
        kind: ProjectionKind.Custom,
        template: '{{ type }}',
        width: 200,
        render: (transaction) => {
          return (
            <Box alignItems="center" flexDirection="row" gap="s8">
              {typeIcon(
                transaction.type,
                transaction.cardPayment?.card?.last4,
                transaction.cardPayment?.card?.creationSource
              )}
            </Box>
          )
        },
      },
      {
        identifier: 'description',
        title: i18n.t('banking.attributes.description'),
        kind: ProjectionKind.Custom,
        template: '{{ description }}',
        render: ({ description }) => {
          return <Text.Normal size="200">{description}</Text.Normal>
        },
      },
      {
        identifier: 'category',
        title: i18n.t('banking.attributes.category'),
        kind: ProjectionKind.Custom,
        template: '{{ category.label }}',
        render: ({ category }) => {
          return (
            <Text.Normal size="200">
              {category?.label ?? i18n.t('banking.attributes.uncategorized')}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amountCents }} {{ currency }}',
        width: 130,
        render: ({ amountCents, currency }) => {
          return (
            <Text.Normal size="200">
              {formattedAmount({
                amountCents: amountCents,
                currency,
                locale,
              })}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'balance',
        title: i18n.t('banking.attributes.balance'),
        kind: ProjectionKind.Custom,
        template: '{{ balanceAfterCents }} {{ currency }}',
        width: 130,
        render: ({ balanceAfterCents, currency }) => {
          return (
            <Text.Normal size="200">
              {formattedAmount({
                amountCents: Number(balanceAfterCents),
                currency,
                locale,
              })}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 80,
        render: (transaction) => {
          return (
            <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
              <Button.Router
                aria-label={i18n.t('banking.accounts.transactions_see_details')}
                to={navigation.finance.banking.accounts.accountId.transactions.transactionId.fullPath(
                  {
                    id: bankAccountId || '',
                    transaction_id: transaction.id,
                  }
                )}
                type="secondary"
                iconName="arrow-right"
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        accountIds: Number(bankAccountId),
        status: 'done',
        description: description,
      },
    }
  )

  useUnsafeEffect(() => {
    if (newNumTransactions != null) {
      setRefreshTimes((old) => old + 1)
    }
  }, [newNumTransactions])

  return (
    <>
      <Outlet />
      <DataTable.Provider
        key={refreshTimes}
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 25,
        }}
        filters={transactionTypeFilter}
        sort={{ path: 'bookingDate', direction: 'desc' }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            disableEditColumns
            search={{
              value: description,
              onChange: (ev) => setDescription(ev.target.value),
              placeholder: i18n.t('banking.accounts.transactions_search_placeholder'),
            }}
            actions={
              reconcileCTA ? (
                <Button
                  onClick={() => {
                    track(trackingEvents.accounting.banking.clickGoToReconcileView)
                    reconcileCTA()
                  }}
                  type="primary"
                >
                  {i18n.t('banking.accounts.actions.reconcile')}
                </Button>
              ) : undefined
            }
            customPopoverOptions={
              <>
                {importTransactionsCTA && (
                  <PopoverMenu.TouchableOption
                    key={'importTransactions'}
                    onClick={importTransactionsCTA}
                    title={i18n.t('banking.accounts.actions.import_transactions')}
                  />
                )}
                {exportTransactionsCTA && (
                  <PopoverMenu.TouchableOption
                    key={'exportTransactions'}
                    onClick={exportTransactionsCTA}
                    title={i18n.t('banking.accounts.actions.export_transactions')}
                  />
                )}
              </>
            }
          />
          {currency && (
            <AccountTransactionsInsights
              locale={locale}
              currency={currency}
              accountIds={[Number(bankAccountId)]}
              description={description}
            />
          )}
          <DataTable.Table emptyState={<EmptyBlock providerConfiguration={configuration} />} />
        </Box>
      </DataTable.Provider>
    </>
  )
}

export default AccountTransactionsDataTable
